import { useEffect, useReducer, useRef } from "react";

/**
 * Adds the `.reveal` base class to the element and toggles `.is-visible`
 * once it scrolls into view. Respects prefers-reduced-motion by marking
 * the element visible immediately rather than waiting on an observer.
 */
export function useScrollReveal<T extends HTMLElement>(threshold = 0.15) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      el.classList.add("is-visible");
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return ref;
}

/** True once the page has scrolled past `offset` pixels. Used to compact the navbar. */
export function useScrolledPast(offset: number): boolean {
  const scrolledRef = useRef(false);
  const [, bump] = useReducer((c: number) => c + 1, 0);

  useEffect(() => {
    const onScroll = () => {
      const past = window.scrollY > offset;
      if (past !== scrolledRef.current) {
        scrolledRef.current = past;
        bump();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [offset]);

  return scrolledRef.current;
}
